import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, ArrowLeft, Check, Store, TrendingDown, TrendingUp, AlertCircle } from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import Header from '../../components/Header';
import apiService from '../../utils/api';
import { useToast } from '../../components/Toast';

function VendorsPage() {
  const toast = useToast();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentView, setCurrentView] = useState('list'); // 'list' | 'form' | 'summary'
  const [editingItem, setEditingItem] = useState(null);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [vendorSummary, setVendorSummary] = useState(null);
  const [filterType, setFilterType] = useState('');

  const emptyForm = { name: '', vendor_type: 'milk', phone: '', address: '', notes: '', credit_term: 30 };
  const [formData, setFormData] = useState(emptyForm);

  const loadVendors = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = filterType ? { vendor_type: filterType } : {};
      const response = await apiService.getVendors(params);
      setVendors(response.data || []);
    } catch (err) {
      setError('Failed to load vendors: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadVendors(); }, [filterType]);

  const handleAdd = () => {
    setEditingItem(null);
    setFormData(emptyForm);
    setCurrentView('form');
  };

  const handleEdit = (vendor) => {
    setEditingItem(vendor);
    setFormData({
      name: vendor.name || '',
      vendor_type: vendor.vendor_type || 'milk',
      phone: vendor.phone || '',
      address: vendor.address || '',
      notes: vendor.notes || '',
      credit_term: vendor.credit_term ?? 30
    });
    setCurrentView('form');
  };

  const handleViewSummary = async (vendor) => {
    try {
      setLoading(true);
      setSelectedVendor(vendor);
      const response = await apiService.getVendorSummary(vendor.vendor_id);
      setVendorSummary(response.data);
      setCurrentView('summary');
    } catch (err) {
      toast.error('Failed to load vendor summary: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (vendor) => {
    if (!window.confirm(`Delete vendor "${vendor.name}"? This cannot be undone.`)) return;
    try {
      setLoading(true);
      await apiService.deleteVendor(vendor.vendor_id);
      await loadVendors();
    } catch (err) {
      toast.error('Failed to delete vendor: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) { toast.warning('Vendor name is required.'); return; }
    if (!formData.vendor_type) { toast.warning('Vendor type is required.'); return; }

    try {
      setLoading(true);
      setError(null);
      if (editingItem) {
        await apiService.updateVendor(editingItem.vendor_id, formData);
      } else {
        await apiService.createVendor(formData);
      }
      await loadVendors();
      setCurrentView('list');
    } catch (err) {
      toast.error('Failed to save vendor: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (v) => `₹${parseFloat(v || 0).toFixed(2)}`;

  const renderLayout = (content) => (
    <div className="app-container">
      <Header />
      <div className="main-content">
        <Sidebar />
        <div className="page-content">{content}</div>
      </div>
    </div>
  );

  // Summary view
  if (currentView === 'summary' && selectedVendor) {
    const s = vendorSummary || {};
    return renderLayout(
      <>
        <div className="page-header">
          <div className="header-left">
            <button className="btn btn-secondary" onClick={() => setCurrentView('list')} style={{ marginRight: '1rem' }}>
              <ArrowLeft size={18} /> Back
            </button>
            <h1 className="page-title"><Store size={22} style={{ marginRight: '0.5rem' }} />{selectedVendor.name}</h1>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
          <div className="card" style={{ padding: '1.2rem', textAlign: 'center' }}>
            <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '0.3rem' }}>Total Purchased</div>
            <div style={{ fontSize: '1.4rem', fontWeight: 700, color: 'var(--error-color)' }}>{formatCurrency(s.total_purchased)}</div>
          </div>
          <div className="card" style={{ padding: '1.2rem', textAlign: 'center' }}>
            <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '0.3rem' }}>Total Paid</div>
            <div style={{ fontSize: '1.4rem', fontWeight: 700, color: 'var(--success-color)' }}>{formatCurrency(s.total_paid)}</div>
          </div>
          <div className="card" style={{ padding: '1.2rem', textAlign: 'center' }}>
            <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '0.3rem' }}>Outstanding</div>
            <div style={{ fontSize: '1.4rem', fontWeight: 700, color: (s.outstanding || 0) > 0 ? 'var(--warning-color)' : 'var(--success-color)' }}>
              {formatCurrency(s.outstanding)}
            </div>
          </div>
        </div>

        <div className="card" style={{ padding: '1.5rem' }}>
          <h3 style={{ marginBottom: '1rem', color: 'var(--primary-color)' }}>Vendor Details</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.8rem' }}>
            <div><span style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>Type</span><div style={{ fontWeight: 600, textTransform: 'capitalize' }}>{selectedVendor.vendor_type}</div></div>
            <div><span style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>Phone</span><div style={{ fontWeight: 600 }}>{selectedVendor.phone || '-'}</div></div>
            <div style={{ gridColumn: '1/-1' }}><span style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>Address</span><div style={{ fontWeight: 600 }}>{selectedVendor.address || '-'}</div></div>
            {selectedVendor.notes && <div style={{ gridColumn: '1/-1' }}><span style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>Notes</span><div>{selectedVendor.notes}</div></div>}
          </div>
        </div>
      </>
    );
  }

  // Form view
  if (currentView === 'form') {
    return renderLayout(
      <>
        <div className="page-header">
          <div className="header-left">
            <button className="btn btn-secondary" onClick={() => setCurrentView('list')} style={{ marginRight: '1rem' }}>
              <ArrowLeft size={18} /> Cancel
            </button>
            <h1 className="page-title">{editingItem ? 'Edit Vendor' : 'Add New Vendor'}</h1>
          </div>
        </div>

        {error && <div className="error-container"><p>{error}</p></div>}

        <div className="card" style={{ maxWidth: '600px', margin: '0 auto' }}>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Vendor Name *</label>
              <input className="form-input" value={formData.name} onChange={e => setFormData(p => ({ ...p, name: e.target.value }))} placeholder="Enter vendor name" disabled={loading} />
            </div>
            <div className="form-group">
              <label className="form-label">Vendor Type *</label>
              <select className="form-select" value={formData.vendor_type} onChange={e => setFormData(p => ({ ...p, vendor_type: e.target.value }))} disabled={loading}>
                <option value="milk">Milk Vendor</option>
                <option value="goods">Goods Vendor</option>
                <option value="animal">Animal Vendor</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Phone</label>
              <input className="form-input" value={formData.phone} onChange={e => setFormData(p => ({ ...p, phone: e.target.value }))} placeholder="Contact number" disabled={loading} />
            </div>
            <div className="form-group">
              <label className="form-label">Credit Term (days)</label>
              <input type="number" min="0" className="form-input" value={formData.credit_term}
                onChange={e => setFormData(p => ({ ...p, credit_term: e.target.value }))}
                placeholder="30" disabled={loading} />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Address</label>
            <textarea className="form-textarea" rows={2} value={formData.address} onChange={e => setFormData(p => ({ ...p, address: e.target.value }))} placeholder="Vendor address" disabled={loading} />
          </div>

          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea className="form-textarea" rows={2} value={formData.notes} onChange={e => setFormData(p => ({ ...p, notes: e.target.value }))} placeholder="Additional notes" disabled={loading} />
          </div>

          <div className="form-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '1.5rem' }}>
            <button className="btn btn-secondary" onClick={() => setCurrentView('list')} disabled={loading}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSubmit} disabled={loading}>
              <Check size={18} /> {loading ? 'Saving...' : editingItem ? 'Update Vendor' : 'Add Vendor'}
            </button>
          </div>
        </div>
      </>
    );
  }

  // List view
  return renderLayout(
    <>
      <div className="page-header">
        <h1 className="page-title"><Store size={24} style={{ marginRight: '0.5rem' }} /> Vendors</h1>
        <button className="btn btn-primary" onClick={handleAdd} disabled={loading}>
          <Plus size={18} /> Add Vendor
        </button>
      </div>

      {error && <div className="error-container"><p>{error}</p></div>}

      <div className="filter-bar">
        <div className="filter-group">
          <label className="form-label" style={{ marginBottom: 0, fontSize: '0.8rem' }}>Type</label>
          <select className="form-select" value={filterType} onChange={e => setFilterType(e.target.value)}>
            <option value="">All Types</option>
            <option value="milk">Milk Vendors</option>
            <option value="goods">Goods Vendors</option>
            <option value="animal">Animal Vendors</option>
          </select>
        </div>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Type</th>
              <th>Phone</th>
              <th>Credit Term</th>
              <th>Address</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {vendors.map(vendor => (
              <tr key={vendor.vendor_id}>
                <td style={{ fontWeight: 600 }}>{vendor.name}</td>
                <td>
                  <span style={{
                    padding: '2px 8px', borderRadius: '12px', fontSize: '0.8rem', fontWeight: 600,
                    background: vendor.vendor_type === 'milk' ? 'var(--primary-light)' : 'var(--bg-secondary)',
                    color: vendor.vendor_type === 'milk' ? 'var(--primary-color)' : 'var(--text-secondary)',
                    textTransform: 'capitalize'
                  }}>
                    {vendor.vendor_type}
                  </span>
                </td>
                <td>{vendor.phone || '-'}</td>
                <td>{vendor.credit_term ?? 30} days</td>
                <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{vendor.address || '-'}</td>
                <td>
                  <div className="actions-cell">
                    <button className="btn-icon" title="View Summary" onClick={() => handleViewSummary(vendor)} disabled={loading}><TrendingDown size={18} /></button>
                    <button className="btn-icon" title="Edit" onClick={() => handleEdit(vendor)} disabled={loading}><Edit size={18} /></button>
                    <button className="btn-icon" style={{ color: 'var(--error-color)' }} title="Delete" onClick={() => handleDelete(vendor)} disabled={loading}><Trash2 size={18} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {vendors.length === 0 && !loading && (
          <div className="no-data">
            <p>No vendors found. Add a vendor to get started.</p>
          </div>
        )}
      </div>
    </>
  );
}

export default VendorsPage;
